import React, { Component } from 'react';
import styled, { css } from 'styled-components';

import ImageContainer from 'components/ImageContainer';
import ProgressBar from 'components/ProgressBar';
import horizontalDots from 'inline-icons/horizontal-dots.svg';
import { DEVICE_PREVIEW_DIMENSIONS as DEVICES } from 'config';

import i18n from 'i18n';

const horizontalCenter = css`
  position: absolute;
  left: 50%;

  ${({ device: { translateLeft = 0 } = {} }) => css`
    transform: translateX(-${50 + translateLeft}%);
  `}
`;

const DeviceContainer = styled.span`
  display: block;

  margin-top: ${({ device: { marginTop } }) => marginTop}px;
`;

const Dots = styled.div.attrs({
  dangerouslySetInnerHTML: { __html: horizontalDots }
})`
  box-sizing: border-box;
  position: absolute;
  top: 10px;
  right: 20px;

  width: 20px;
`;

const BaseFrame = styled.div`
  position: relative;
`;

const DeviceFrame = styled(BaseFrame)`
  height: ${({ device: { frameHeight } }) => frameHeight}px;
`;

const getDeviceImageUrl = (deviceName) =>
  `/device-images/${deviceName.replace(' ', '_')}.png`;

const DeviceImage = styled.div`
  height: 100%;
  width: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  ${({ deviceName }) => `
    background-image: url(${getDeviceImageUrl(deviceName)});
  `}
`;

const BaseClockImageContainer = styled.div`
  top: 50%;
  ${horizontalCenter}
`;

const DeviceClockImageContainer = styled(BaseClockImageContainer)`
  ${({ device: { top, width } }) => `
    top: ${top}px;

    width: ${width}px;
  `}
`;

const HiddenPreloadImage = styled.img`
  position: absolute;
  left: -99999;
  height: 0px;
  width: 0px;

  opacity: 0;
`;

const HiddenPreloadContent = styled.div`
  opacity: ${({ loaded }) => (loaded ? 1 : 0)};
`;

class PreloadDeviceImage extends Component {
  state = {
    loaded: false
  };

  handleImageLoaded = () =>
    this.setState({
      loaded: true
    });

  render() {
    const { deviceName, children } = this.props;
    const { loaded } = this.state;

    return (
      <div>
        <HiddenPreloadImage
          src={getDeviceImageUrl(deviceName)}
          onLoad={this.handleImageLoaded}
          alt=""
        />
        <HiddenPreloadContent loaded={loaded}>{children}</HiddenPreloadContent>
      </div>
    );
  }
}

const ProgressContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  background-color: black;
`;

const ProgressTextWrapper = styled.div`
  top: calc(50% - 15px);
  ${horizontalCenter}

  font-size: 16px;
  color: #a0a8ac;
`;

const ProgressBarWrapper = styled.div`
  top: calc(50% + 15px);
  ${horizontalCenter}

  width: 78px;
`;

const ProgressOrLoading = (props) => (
  <ProgressContainer>
    <ProgressTextWrapper>
      {i18n('page.ClockGallery.MyClocks.switching')()}
    </ProgressTextWrapper>
    <ProgressBarWrapper>
      <ProgressBar {...props} />
    </ProgressBarWrapper>
  </ProgressContainer>
);

const Progress = ({ clock }) => {
  if (clock.showProgress) {
    return <ProgressOrLoading percentage={clock.progress} />;
  }

  if (
    clock.isPending ||
    clock.isInstalling ||
    (clock.isSelected && !clock.isActive)
  ) {
    return <ProgressOrLoading animation />;
  }

  return null;
};

const Device = ({ deviceName, clock, clock: { banners: [image] = [{}] } }) => {
  const device = DEVICES[deviceName];

  const imageContainer = image && (
    <ImageContainer
      containerAttr={{
        width: image.width,
        height: image.height
      }}
      imageAttr={{
        alt: clock.name,
        src: image.uri
      }}
    />
  );

  if (!device) {
    return (
      <div>
        <Dots />
        <BaseFrame>
          <BaseClockImageContainer>
            {imageContainer}
            <Progress clock={clock} />
          </BaseClockImageContainer>
        </BaseFrame>
      </div>
    );
  }

  return (
    <PreloadDeviceImage deviceName={deviceName}>
      <DeviceContainer device={device}>
        <Dots />
        <DeviceFrame device={device}>
          <DeviceImage deviceName={deviceName} />
          <DeviceClockImageContainer device={device}>
            {imageContainer}
            <Progress clock={clock} device={device} />
          </DeviceClockImageContainer>
        </DeviceFrame>
      </DeviceContainer>
    </PreloadDeviceImage>
  );
};

export default Device;
